/**
 * Advanced Filter Zod Schema
 *
 * This is the single source of truth for advanced filtering validation.
 * Used by both client and server to ensure type safety and consistent validation.
 */

import { z } from 'zod';

// ============================================================================
// Enums
// ============================================================================

export const FilterFieldEnum = z.enum([
    'search',
    'age',
    'gender',
    'race',
    'ethnicity',
    'diagnosis',
    'recordType',
    'dateRange',
]);

export const FilterOperatorEnum = z.enum(['equals', 'contains', 'between', 'greaterThan', 'lessThan', 'in']);

export const LogicalOperatorEnum = z.enum(['AND', 'OR']);

// ============================================================================
// Discriminated Union for Type-Safe Filter Conditions
// ============================================================================

/**
 * Search field: accepts string or array of strings for multiple search terms
 */
export const SearchConditionSchema = z.object({
    field: z.literal('search'),
    operator: z.literal('contains').optional(),
    value: z.union([z.string().trim().min(1), z.array(z.string().min(1)).min(1)]),
});

export const DocumentSearchConditionSchema = z.object({
    field: z.literal('documentSearch'),
    operator: z.literal('contains').optional(),
    value: z.union([z.string().trim().min(1), z.array(z.string().min(1)).min(1)]),
});

/**
 * Age field: accepts number (for equals/greaterThan/lessThan) or tuple (for between)
 */
export const AgeConditionSchema = z.object({
    field: z.literal('age'),
    operator: FilterOperatorEnum,
    value: z.union([
        z.number().int().min(0).max(120), // Single age value
        z.tuple([z.number().int().min(0).max(120), z.number().int().min(0).max(120)]), // Age range
    ]),
});

/**
 * Gender field: accepts array of gender values or single gender string
 */
export const GenderConditionSchema = z.object({
    field: z.literal('gender'),
    operator: z.enum(['equals', 'in']).optional(),
    value: z.union([
        z.enum(['Male', 'Female', 'Other']), // Single gender value
        z.array(z.enum(['Male', 'Female', 'Other'])).min(1), // Multiple genders
    ]),
});

/**
 * Race field: accepts array of race values or single race string
 */
export const RaceConditionSchema = z.object({
    field: z.literal('race'),
    operator: z.enum(['equals', 'in']).optional(),
    value: z.union([
        z.string().min(1), // Single race value
        z.array(z.string().min(1)).min(1), // Multiple races
    ]),
});

/**
 * Ethnicity field: accepts array of ethnicity values or single ethnicity string
 */
export const EthnicityConditionSchema = z.object({
    field: z.literal('ethnicity'),
    operator: z.enum(['equals', 'in']).optional(),
    value: z.union([
        z.string().min(1), // Single ethnicity value
        z.array(z.string().min(1)).min(1), // Multiple ethnicities
    ]),
});

/**
 * Diagnosis field: accepts array of diagnosis values or single diagnosis string
 */
export const DiagnosisConditionSchema = z.object({
    field: z.literal('diagnosis'),
    operator: z.enum(['equals', 'contains', 'in']).optional(),
    value: z.union([
        z.string().min(1), // Single diagnosis value
        z.array(z.string().min(1)).min(1), // Multiple diagnoses
    ]),
});

/**
 * Record Type field: accepts array of record type strings
 */
export const RecordTypeConditionSchema = z.object({
    field: z.literal('recordType'),
    operator: z.literal('in').optional(),
    value: z.array(z.string().min(1)).min(1),
});

/**
 * Date Range field: accepts tuple of ISO date strings
 */
export const DateRangeConditionSchema = z.object({
    field: z.literal('dateRange'),
    operator: z.literal('between').optional(),
    value: z.tuple([
        z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD format'),
        z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be YYYY-MM-DD format'),
    ]),
});

/**
 * Discriminated union of all filter condition types
 * TypeScript will automatically narrow the type based on the 'field' property
 */
export const FilterConditionSchema = z.discriminatedUnion('field', [
    SearchConditionSchema,
    DocumentSearchConditionSchema,
    AgeConditionSchema,
    GenderConditionSchema,
    RaceConditionSchema,
    EthnicityConditionSchema,
    DiagnosisConditionSchema,
    RecordTypeConditionSchema,
    DateRangeConditionSchema,
]);

// ============================================================================
// Filter Group Schema
// ============================================================================

export const FilterGroupSchema = z.object({
    operator: LogicalOperatorEnum,
    conditions: z.array(FilterConditionSchema).min(1, 'Filter group must have at least one condition'),
});

// ============================================================================
// Advanced Filter Schema
// ============================================================================

export const AdvancedFilterSchema = z.object({
    groups: z.array(FilterGroupSchema).min(1, 'Advanced filter must have at least one group'),
    groupOperator: z.enum(['AND', 'OR']).optional(),
});

// ============================================================================
// TypeScript Type Exports (inferred from schemas)
// ============================================================================

export type FilterField = z.infer<typeof FilterFieldEnum>;
export type FilterOperator = z.infer<typeof FilterOperatorEnum>;
export type LogicalOperator = z.infer<typeof LogicalOperatorEnum>;
export type FilterCondition = z.infer<typeof FilterConditionSchema>;
export type FilterGroup = z.infer<typeof FilterGroupSchema>;
export type AdvancedFilter = z.infer<typeof AdvancedFilterSchema>;

// ============================================================================
// Constants for backward compatibility
// ============================================================================

export const FilterField = {
    SEARCH: 'search',
    DOCUMENT_SEARCH: 'documentSearch',
    AGE: 'age',
    GENDER: 'gender',
    RACE: 'race',
    ETHNICITY: 'ethnicity',
    DIAGNOSIS: 'diagnosis',
    RECORD_TYPE: 'recordType',
    DATE_RANGE: 'dateRange',
} as const;

export const FilterOperator = {
    EQUALS: 'equals',
    CONTAINS: 'contains',
    BETWEEN: 'between',
    GREATER_THAN: 'greaterThan',
    LESS_THAN: 'lessThan',
    IN: 'in',
} as const;

export const LogicalOperator = {
    AND: 'AND',
    OR: 'OR',
} as const;
